/**
 * PATH: packages/email-hub/src/feature/components/snapThreadToLatest.ts
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-SNAPTHREAD-001
 * PURPOSE: Put the reading pane on the latest message when an email opens.
 * ROLE IN ARCHITECTURE: Pure scroll helpers for the stacked EmailThread reader.
 * MAIN EXPORTS: findOverflowYParent, distanceFromScrollEnd, snapAnchorToEnd
 * NON-RESPONSIBILITIES: Does not choose which message is latest, and does not
 *   listen for the operator scrolling back up. EmailThread owns that.
 * NOTES FOR FUTURE AI: The chain is oldest-first. Snap the scroll parent to
 *   its end so the newest message (and the reply bar under it) is in view.
 *   Padding inside the scroller is not enough: the pane starts at scrollTop 0.
 */

/** How far from the bottom still counts as "on the latest message". */
export const THREAD_SNAP_SLACK_PX = 80

/**
 * Walk up from a message node to the pane that actually scrolls.
 *
 * Inputs: `start` — the latest message element, or null.
 * Output: the nearest ancestor with overflow-y auto/scroll, or null when the
 *   message is not inside a scroller yet.
 * Side effects: none. Failure: returns null (caller may scrollIntoView).
 */
export function findOverflowYParent(start: HTMLElement | null): HTMLElement | null {
  let node = start?.parentElement ?? null
  while (node) {
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return node
    }
    node = node.parentElement
  }
  return null
}

/**
 * Pixels between the current scroll position and the bottom of `scroller`.
 * Zero means the latest message is already in view.
 */
export function distanceFromScrollEnd(scroller: HTMLElement): number {
  return scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop
}

/**
 * Move the reading pane so the bottom of the chain (the latest message) is
 * visible. Uses the overflow parent when there is one, because that is the
 * element the operator actually scrolls.
 *
 * Side effects: sets scrollTop, or calls scrollIntoView. jsdom's
 * scrollIntoView throws; that throw is swallowed so tests can render the
 * thread outside a scroller.
 */
export function snapAnchorToEnd(anchor: HTMLElement): void {
  const scroller = findOverflowYParent(anchor)
  if (scroller) {
    scroller.scrollTop = scroller.scrollHeight
    return
  }
  try {
    anchor.scrollIntoView?.({ block: 'end' })
  } catch {
    // jsdom implements scrollIntoView by throwing.
  }
}
